/**
 * @module AdapterCard
 * @package @geenius/adapters/react
 * @description Renders the Tailwind-based adapter summary card for the React
 * variant. The card surfaces provider status, latency, and domain metadata for
 * one adapter domain in the dashboard views.
 */

import type { AdapterDomain, AdapterStatusInfo } from "@geenius/adapters";
import { DOMAIN_DESCRIPTIONS, DOMAIN_LABELS } from "@geenius/adapters";
import type { JSX } from "react";
import { cn } from "../lib/utils";
import { AdapterDomainIcon } from "./AdapterDomainIcon";
import { AdapterStatusBadge } from "./AdapterStatusBadge";

/**
 * Props for rendering a single adapter summary card in the React dashboard grid.
 *
 * @property domain Adapter domain represented by the card.
 * @property status Current provider health and metadata for the domain.
 * @property onClick Optional callback fired when the card is activated.
 * @property className Optional class name merged into the card container.
 */
export interface AdapterCardProps {
  domain: AdapterDomain;
  status: AdapterStatusInfo;
  onClick?: () => void;
  className?: string;
}

/**
 * Renders a clickable React summary card for one adapter domain.
 *
 * @param props Card props including the adapter domain, status payload, and click handler.
 * @returns A button element that summarizes provider state for the selected domain.
 */
export function AdapterCard({
  domain,
  status,
  onClick,
  className,
}: AdapterCardProps): JSX.Element {
  const label = `${DOMAIN_LABELS[domain]} adapter - ${status.status}`;
  const cardClassName = cn(
    "relative flex w-full items-start gap-4 overflow-hidden rounded-lg border border-border bg-card p-5 text-left text-card-foreground shadow-sm transition-colors duration-200 motion-reduce:transition-none",
    onClick &&
      "group hover:border-primary/20 hover:bg-accent/5 hover:shadow-md active:border-primary/30 active:bg-accent/10 active:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    className,
  );
  const content = (
    <>
      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md border border-border bg-muted text-2xl shadow-sm transition-transform duration-300 group-hover:scale-105 group-active:scale-105 motion-reduce:transform-none motion-reduce:transition-none">
        <AdapterDomainIcon domain={domain} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <h3 className="truncate text-sm font-bold text-foreground">
            {DOMAIN_LABELS[domain]}
          </h3>
          <AdapterStatusBadge status={status.status} />
        </div>

        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
          {DOMAIN_DESCRIPTIONS[domain]}
        </p>

        {status.provider && status.provider !== "none" && (
          <div className="mt-2 flex items-center gap-2">
            <span className="rounded-md border border-border bg-muted px-2 py-0.5 text-[0.625rem] font-semibold text-muted-foreground">
              {status.provider}
            </span>
            {status.latency !== undefined && (
              <span className="text-[0.625rem] text-muted-foreground">
                {status.latency}ms
              </span>
            )}
          </div>
        )}

        {status.error && (
          <p className="mt-1.5 truncate text-[0.625rem] text-destructive">
            {status.error}
          </p>
        )}
      </div>
    </>
  );

  if (!onClick) {
    return (
      <article className={cardClassName} aria-label={label}>
        {content}
      </article>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cardClassName}
      aria-label={label}
    >
      {content}
    </button>
  );
}
